import React from "react";
import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Heading,
  Text,
  useBreakpointValue,
  Container,
} from "@chakra-ui/react";
import { ChevronRightIcon } from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";

const ServiceDetailsHeader = ({
  title,
  subtitle,
  backgroundImage,
  breadcrumbItems,
  topRightComponent, // New prop for top-right component
}) => {
  const navigate = useNavigate();

  // Comprehensive responsive font sizes for all devices
  const headingFontSize = useBreakpointValue({
    base: "xl", // Mobile phones (320px+)
    sm: "xl", // Large phones (480px+)
    md: "2xl", // Tablets (768px+)
    lg: "3xl", // Small laptops (992px+)
    xl: "4xl", // Desktop (1280px+)
    "2xl": "4xl", // Large screens (1536px+)
  });

  // Responsive subtitle font sizes
  const subtitleFontSize = useBreakpointValue({
    base: "xs", // Mobile phones
    sm: "sm", // Large phones
    md: "md", // Tablets
    lg: "lg", // Small laptops
    xl: "lg", // Desktop
    "2xl": "xl", // Large screens
  });

  const breadcrumbFontSize = useBreakpointValue({
    base: "xs", // Very small on mobile
    sm: "sm", // Small phones
    md: "md", // Tablets and up
    lg: "md", // Desktop
  });

  // Responsive heights for different devices - increased to accommodate subtitle
  const bannerHeight = useBreakpointValue({
    base: subtitle ? "140px" : "120px", // Mobile - compact
    sm: subtitle ? "160px" : "140px", // Large phones
    md: subtitle ? "220px" : "200px", // Tablets
    lg: subtitle ? "250px" : "230px", // Laptops
    xl: subtitle ? "280px" : "250px", // Desktop
    "2xl": subtitle ? "320px" : "280px", // Large screens
  });

  // Responsive padding and spacing
  const containerPadding = useBreakpointValue({
    base: 4, // Mobile
    sm: 6, // Large phones
    md: 8, // Tablets
    lg: 10, // Desktop
    xl: 12, // Large screens
  });

  return (
    <Box
      bgImage={`url('${backgroundImage}')`}
      bgPosition="center"
      bgRepeat="no-repeat"
      bgSize="cover"
      position="relative"
      h={bannerHeight}
      color="white"
      display="flex"
      flexDirection="column"
      overflow="hidden"
    >
      {/* Responsive overlay with better mobile optimization */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        backgroundImage={backgroundImage}
        zIndex={0}
        backgroundSize={"cover"}
      />

      <Container
        maxW="7xl"
        position="relative"
        zIndex={1}
        h="full"
        display="flex"
        flexDirection="column"
        px={containerPadding}
      >
        {/* Top Right Component */}
        {topRightComponent && (
          <Box
            position="absolute"
            top={{ base: 4, sm: 6, md: 8 }}
            right={{ base: 4, sm: 6, md: 8 }}
            zIndex={10}
          >
            {topRightComponent}
          </Box>
        )}

        <Box pt={{ base: 3, sm: 4, md: 4 }} pb={{ base: 1, sm: 2 }}>
          <Breadcrumb
            fontSize={breadcrumbFontSize}
            separator={<ChevronRightIcon color="gray.200" />}
            opacity={0.95}
            spacing={{ base: 1, sm: 2 }}
          >
            {breadcrumbItems.map((item, index) => (
              <BreadcrumbItem key={index} isCurrentPage={item.isCurrentPage}>
                <BreadcrumbLink
                  as="button"
                  onClick={() => {
                    if (!item.isCurrentPage && item.href) {
                      navigate(item.href);
                    }
                  }}
                  _hover={{ textDecoration: "underline" }}
                  color="white"
                  whiteSpace="nowrap"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  maxW={{ base: "80px", sm: "120px", md: "none" }}
                  aria-current={item.isCurrentPage ? "page" : undefined}
                >
                  {item.label}
                </BreadcrumbLink>
              </BreadcrumbItem>
            ))}
          </Breadcrumb>
        </Box>

        {/* Responsive Main Title and Subtitle */}
        <Box
          flex="1"
          display="flex"
          alignItems="center"
          justifyContent="center"
          pb={{ base: 4, sm: 5, md: 6 }}
          px={{ base: 2, sm: 4, md: 6 }}
        >
          <Box textAlign="center" maxW={"100%"} w="full">
            <Heading
              fontSize={headingFontSize}
              fontWeight="bold"
              lineHeight="shorter"
              letterSpacing={{ base: "normal", lg: "tight" }}
              mb={subtitle ? { base: 2, sm: 3, md: 4 } : 0}
            >
              {title}
            </Heading>

            {subtitle && (
              <Text
                fontSize={subtitleFontSize}
                fontWeight="medium"
                lineHeight="base"
                opacity={0.9}
                color="gray.100"
                maxW="100%"
              >
                {subtitle}
              </Text>
            )}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default ServiceDetailsHeader;
